import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { cx } from './shared';
import { Surface } from './Surface';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  children?: ReactNode;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  title?: ReactNode;
  value?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
}

const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    children,
    className,
    padding = 'md',
    title,
    value,
    description,
    icon,
    ...rest
  }: CardProps,
  ref,
) {
  const showMeta = [title, value, description, icon].some((item) => item != null);

  return (
    <Surface ref={ref} padding={padding} className={className} {...rest}>
      {showMeta ? (
        <div className="flex items-start gap-field-gap">
          {icon != null ? (
            <div className="text-section-title leading-none text-accent" aria-hidden="true">
              {icon}
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            {title != null ? (
              <h3 className="text-subsection-title font-semibold text-ink-strong">{title}</h3>
            ) : null}
            {value != null ? (
              <div className="so-data-text mt-1 text-metric font-semibold text-ink-strong">
                {value}
              </div>
            ) : null}
            {description != null ? (
              <p className="mt-1 text-body text-ink-muted">{description}</p>
            ) : null}
          </div>
        </div>
      ) : null}
      {children != null ? <div className={cx(showMeta && 'mt-4')}>{children}</div> : null}
    </Surface>
  );
});

Card.displayName = 'Card';

export default Card;
